/**
 * Storage Configuration
 *
 * Bucket settings, file size limits, and validation helpers.
 */

import type { StorageBucket } from './types';

/**
 * Maximum file size limits per bucket type (in bytes)
 */
export const FILE_SIZE_LIMITS = {
  default: 50 * 1024 * 1024, // 50MB
  pitchDeck: 100 * 1024 * 1024, // 100MB for pitch decks
  avatar: 5 * 1024 * 1024, // 5MB for avatars
};

/**
 * Allowed MIME types per bucket
 */
export const ALLOWED_MIME_TYPES: Record<StorageBucket, string[]> = {
  dataroom: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-powerpoint',
    'image/png',
    'image/jpeg',
    'text/csv',
  ],
  // Legacy binary .ppt (application/vnd.ms-powerpoint) is intentionally excluded:
  // Mistral's OCR/Files API cannot ingest it (not in its supported-mimetype
  // allow-list), so it would silently produce an empty extraction. Founders
  // export as PDF or modern .pptx instead.
  'pitch-decks': [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
  avatars: ['image/png', 'image/jpeg', 'image/webp'],
  'submission-decks': [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
};

/**
 * MIME type to file extension mapping
 */
export const MIME_TO_EXTENSION: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'application/vnd.ms-powerpoint': '.ppt',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
  'text/csv': '.csv',
};

/**
 * Validate a file against bucket constraints
 */
export function validateFile(
  file: { size?: number; mimeType?: string },
  bucket: StorageBucket
): { valid: boolean; error?: string } {
  const maxSize =
    bucket === 'avatars'
      ? FILE_SIZE_LIMITS.avatar
      : bucket === 'pitch-decks'
        ? FILE_SIZE_LIMITS.pitchDeck
        : FILE_SIZE_LIMITS.default;

  if (file.size && file.size > maxSize) {
    return {
      valid: false,
      error: `File too large. Maximum size is ${Math.round(maxSize / 1024 / 1024)}MB`,
    };
  }

  const allowedTypes = ALLOWED_MIME_TYPES[bucket];
  if (file.mimeType && !allowedTypes.includes(file.mimeType)) {
    return { valid: false, error: 'File type not allowed for this bucket' };
  }

  return { valid: true };
}
